import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, AlertCircle, Ship, Package, Anchor, Download, CheckCircle, XCircle } from 'lucide-react';
import * as XLSX from 'xlsx';

// ============================================================================
// PRODUCTION TRACKING BETA - Booking + Production WIP joined view
// ============================================================================
export default function ProductionTrackingBetaDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [filters, setFilters] = useState({ divisions: [], vendors: [], stages: [], seasons: [] });
  const [rowCount, setRowCount] = useState(0);

  // Filter state
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);

  // Format helpers
  const fmt = (v) => {
    if (v === null || v === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(Math.round(v));
  };

  const fmtCur = (v) => {
    if (v === null || v === undefined || v === 0) return '$0';
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmtDec = (v) => {
    if (v === null || v === undefined || v === 0) return '0';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'getProductionTrackingBeta', { timeout: 150000 });
      const result = await fn({
        division: selectedDivision,
        vendor: selectedVendor,
        stage: selectedStage,
        season: selectedSeason
      });
      const d = result.data;
      setData(d.data || []);
      setTotals(d.totals || {});
      setFilters(d.filters || { divisions: [], vendors: [], stages: [], seasons: [] });
      setRowCount(d.rowCount || 0);
    } catch (err) {
      console.error('Error fetching production tracking beta data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, selectedVendor, selectedStage, selectedSeason]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => {
    setSelectedDivision(null);
    setSelectedVendor(null);
    setSelectedStage(null);
    setSelectedSeason(null);
  };

  const hasActiveFilters = selectedDivision || selectedVendor || selectedStage || selectedSeason;

  // Export to Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const exportData = data.map(r => ({
      'Booking': r.Booking || '',
      'PO': r.PO || '',
      'PO Line': r.PO_Line || '',
      'Line': r.Line,
      'Style': r.Style || '',
      'Vendor': r.Vendor || '',
      'Division': r.Division_Name || r.Division || '',
      'Booking Qty': r.Total_Qty,
      'Price': r.Price,
      'Booking Amount': r.Total_Amount,
      'CBM': r.Total_CBM,
      'Cartons': r.Total_Cartons,
      'Port of Loading': r.Port_of_Loading || '',
      'Place of Receipt': r.Place_of_Receipt || '',
      'Cargo Turnover': r.Cargo_Turnover_Date || '',
      'ETD POR': r.ETD_POR || '',
      'ETD POL': r.ETD_POL || '',
      'ETA': r.ETA || '',
      'Prod Stage': r.stages || '',
      'Factory': r.factories || '',
      'Container': r.containers || '',
      'Prod Season': r.seasons || '',
      'Prod Qty': r.prod_qty,
      'FOB Cost': r.Total_FOB_Cost,
      'Landed Cost': r.Total_Cost,
      'Sales': r.Total_Sales,
      'GP': r.Total_GP,
      'Earliest Ship': r.earliest_ship_date || '',
      'Latest Ship': r.latest_ship_date || ''
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportData), 'Booking+Production');
    XLSX.writeFile(wb, `Production_Tracking_Beta_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-lg px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Ship size={20} className="text-violet-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Production Tracking (Beta)</h2>
            <p className="text-xs text-slate-500">Booking data joined with production WIP</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && (
            <span className="text-xs text-slate-400">{fmt(rowCount)} rows</span>
          )}
          <button onClick={handleExportExcel} disabled={loading || data.length === 0} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
            <Download size={12} /> Excel
          </button>
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && !error && totals && (
        <div className="grid grid-cols-6 gap-3">
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Anchor size={12} className="text-violet-500" />
              <p className="text-[10px] text-slate-500 font-medium">Bookings</p>
            </div>
            <p className="text-lg font-bold text-slate-800">{fmt(totals.totalBookings)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Package size={12} className="text-blue-500" />
              <p className="text-[10px] text-slate-500 font-medium">POs</p>
            </div>
            <p className="text-lg font-bold text-slate-800">{fmt(totals.totalPOs)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-slate-500 font-medium mb-1">Booking Qty</p>
            <p className="text-lg font-bold text-slate-800">{fmt(totals.totalBookingQty)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-slate-500 font-medium mb-1">CBM</p>
            <p className="text-lg font-bold text-slate-800">{fmtDec(totals.totalCBM)}</p>
          </div>
          <div className="bg-violet-50 rounded-lg border border-violet-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-violet-600 font-medium mb-1">FOB Cost</p>
            <p className="text-lg font-bold text-violet-700">{fmtCur(totals.totalFOBCost)}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 text-center shadow-sm">
            <p className="text-[10px] text-blue-600 font-medium mb-1">Booking Amount</p>
            <p className="text-lg font-bold text-blue-700">{fmtCur(totals.totalBookingAmount)}</p>
          </div>
        </div>
      )}

      {/* Match Status */}
      {!loading && !error && totals && (
        <div className="flex items-center gap-4 px-1">
          <div className="flex items-center gap-1.5">
            <CheckCircle size={14} className="text-green-500" />
            <span className="text-xs text-slate-600">
              <span className="font-semibold text-green-700">{fmt(totals.matchedRows)}</span> matched to production
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <XCircle size={14} className="text-amber-500" />
            <span className="text-xs text-slate-600">
              <span className="font-semibold text-amber-700">{fmt(totals.unmatchedRows)}</span> no production match
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="px-4 py-2.5 flex items-center gap-4 flex-wrap">
          {filters.divisions && filters.divisions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Division:</span>
              <select value={selectedDivision || ''} onChange={(e) => setSelectedDivision(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                <option value="">All</option>
                {filters.divisions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          {filters.vendors && filters.vendors.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Vendor:</span>
              <select value={selectedVendor || ''} onChange={(e) => setSelectedVendor(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                <option value="">All</option>
                {filters.vendors.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          )}
          {filters.stages && filters.stages.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Stage:</span>
              <select value={selectedStage || ''} onChange={(e) => setSelectedStage(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                <option value="">All</option>
                {filters.stages.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {filters.seasons && filters.seasons.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Season:</span>
              <select value={selectedSeason || ''} onChange={(e) => setSelectedSeason(e.target.value || null)} className="text-xs border border-slate-200 rounded px-2 py-1">
                <option value="">All</option>
                {filters.seasons.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Detail Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-white flex items-center gap-2">
          <Package size={16} className="text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-800">Booking + Production Detail</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={20} className="animate-spin text-violet-500" />
            <span className="ml-2 text-sm text-slate-600">Loading data...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <AlertCircle size={20} className="text-red-500 mr-2" />
            <span className="text-sm text-red-600">{error}</span>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <span className="text-sm text-slate-500">No data found</span>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: '600px' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">Booking</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">PO</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Line</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Style</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Vendor</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Division</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Booking Qty</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">CBM</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">ETD POL</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">ETA</th>
                  <th className="px-2 py-2 text-center font-semibold text-violet-600 border-b border-slate-200 bg-violet-50/50">Stage</th>
                  <th className="px-2 py-2 text-left font-semibold text-violet-600 border-b border-slate-200 bg-violet-50/50">Factory</th>
                  <th className="px-2 py-2 text-left font-semibold text-violet-600 border-b border-slate-200 bg-violet-50/50">Container</th>
                  <th className="px-2 py-2 text-right font-semibold text-violet-600 border-b border-slate-200 bg-violet-50/50">Prod Qty</th>
                  <th className="px-2 py-2 text-right font-semibold text-cyan-600 border-b border-slate-200 bg-cyan-50/50">FOB Cost</th>
                  <th className="px-2 py-2 text-right font-semibold text-blue-600 border-b border-slate-200 bg-blue-50/50">Landed Cost</th>
                  <th className="px-2 py-2 text-right font-semibold text-green-600 border-b border-slate-200 bg-green-50/50">Sales</th>
                  <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">GP</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => {
                  const hasMatch = !!row.stages;
                  return (
                    <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${!hasMatch ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-2 py-1.5 font-medium text-slate-700 whitespace-nowrap">{row.Booking || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{row.PO || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{row.Line ?? '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600">{row.Style || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{row.Vendor || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{row.Division_Name || row.Division || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{fmt(row.Total_Qty)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{fmtDec(row.Total_CBM)}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(row.ETD_POL)}</td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(row.ETA)}</td>
                      <td className="px-2 py-1.5 text-center">
                        {hasMatch ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            row.stages === 'SHIPPED' ? 'bg-cyan-100 text-cyan-700' :
                            row.stages === 'ISSUED' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{row.stages}</span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-violet-600 whitespace-nowrap">{row.factories || '-'}</td>
                      <td className="px-2 py-1.5 text-violet-600 whitespace-nowrap">{row.containers || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-violet-600">{hasMatch ? fmt(row.prod_qty) : '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-cyan-700">{hasMatch ? fmtCur(row.Total_FOB_Cost) : '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-blue-700">{hasMatch ? fmtCur(row.Total_Cost) : '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-green-600">{hasMatch ? fmtCur(row.Total_Sales) : '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-purple-600">{hasMatch ? fmtCur(row.Total_GP) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                <tr className="font-semibold text-slate-700">
                  <td className="px-2 py-2" colSpan={6}>TOTAL</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(totals.totalBookingQty)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmtDec(totals.totalCBM)}</td>
                  <td colSpan={2}></td>
                  <td colSpan={3}></td>
                  <td className="px-2 py-2 text-right tabular-nums text-violet-700">{fmt(totals.totalProdQty)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-cyan-700">{fmtCur(totals.totalFOBCost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-bold text-blue-700">{fmtCur(totals.totalLandedCost)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-green-600">{fmtCur(totals.totalSales)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-purple-600">{fmtCur(totals.totalGP)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
